import { AlertCircle, CheckCircle, Clock, Film, HardDrive, Loader2, Pause, Play, RefreshCw, Tv } from "lucide-react";
import { useCallback, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shared/components/ui/card";
import { useEncodingStatus, type EncodingEntry, type EncodingStats } from "./hooks/useEncodingStatus";
import { POLLING_INTERVALS } from "./hooks/usePolling";

/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */

/**
 * Variant type for status indicators
 */
type StatusVariant = "default" | "success" | "warning" | "error";

/**
 * Stats card component for the encoding dashboard
 */
interface StatsCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  variant?: StatusVariant;
}

function StatsCard({ title, value, description, icon, variant = "default" }: Readonly<StatsCardProps>) {
  const variantStyles = {
    default: "text-neutral-400",
    success: "text-emerald-400",
    warning: "text-amber-400",
    error: "text-red-400",
  };

  return (
    <Card className="bg-transparent border-neutral-800">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-neutral-300">{title}</CardTitle>
        <div className={variantStyles[variant]}>{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-white">{value}</div>
        {description && <p className="text-xs text-neutral-500 mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * Get the variant for success rate based on percentage
 */
function getSuccessRateVariant(successRate: number | null): StatusVariant {
  if (successRate === null) return "default";
  if (successRate >= 90) return "success";
  if (successRate >= 70) return "warning";
  return "error";
}

/**
 * Stats panel showing encoding queue metrics
 */
function EncodingStatsPanel({ stats }: Readonly<{ stats: EncodingStats }>) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatsCard
        title="Queue Length"
        value={stats.queueLength}
        description={`${stats.activeEncoding} encoding, ${stats.pending + stats.uploaded} waiting`}
        icon={<HardDrive className="h-4 w-4" />}
        variant={stats.queueLength > 10 ? "warning" : "default"}
      />
      <StatsCard
        title="Completed Today"
        value={stats.completedToday}
        icon={<CheckCircle className="h-4 w-4" />}
        variant="success"
      />
      <StatsCard
        title="Failed"
        value={stats.failedTotal}
        description="Total failed encodings"
        icon={<AlertCircle className="h-4 w-4" />}
        variant={stats.failedTotal > 0 ? "error" : "default"}
      />
      <StatsCard
        title="Success Rate"
        value={stats.successRatePercent === null ? "N/A" : `${stats.successRatePercent}%`}
        description={
          stats.avgEncodingTimeSec === null ? "No data yet" : `Avg ${Math.round(stats.avgEncodingTimeSec / 60)} min`
        }
        icon={<Tv className="h-4 w-4" />}
        variant={getSuccessRateVariant(stats.successRatePercent)}
      />
    </div>
  );
}

/**
 * Individual encoding entry row
 */
interface EncodingEntryRowProps {
  entry: EncodingEntry;
  onRetry?: (id: string) => Promise<boolean>;
  onView?: (submissionId: string) => void;
  showRetry?: boolean;
}

function EncodingEntryRow({ entry, onRetry, onView, showRetry = false }: Readonly<EncodingEntryRowProps>) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (!onRetry) return;
    setRetrying(true);
    try {
      await onRetry(entry.id);
    } finally {
      setRetrying(false);
    }
  };

  const handleView = () => {
    onView?.(entry.submissionId);
  };

  // Format file size
  const formatSize = (size: string | null): string => {
    if (!size) return "Unknown";
    const bytes = Number.parseInt(size, 10);
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Format duration
  const formatDuration = (sec: number | null): string => {
    if (sec === null) return "Unknown";
    const hours = Math.floor(sec / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    const secs = Math.floor(sec % 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  // Status badge
  const statusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    PENDING: {
      label: "Pending",
      className: "bg-neutral-700 text-neutral-300",
      icon: <Clock className="h-3 w-3" />,
    },
    UPLOADING: {
      label: "Uploading",
      className: "bg-indigo-900/50 text-indigo-400",
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    UPLOADED: {
      label: "Uploaded",
      className: "bg-blue-900/50 text-blue-400",
      icon: <HardDrive className="h-3 w-3" />,
    },
    ENCODING: {
      label: "Encoding",
      className: "bg-amber-900/50 text-amber-400",
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    READY: {
      label: "Complete",
      className: "bg-emerald-900/50 text-emerald-400",
      icon: <CheckCircle className="h-3 w-3" />,
    },
    ERRORED: {
      label: "Failed",
      className: "bg-red-900/50 text-red-400",
      icon: <AlertCircle className="h-3 w-3" />,
    },
  };

  const status = statusConfig[entry.encodingStatus] || statusConfig.PENDING;

  return (
    <tr className="border-b border-neutral-800 hover:bg-neutral-800/30 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <Film className="h-4 w-4 text-neutral-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white truncate max-w-[200px]">{entry.submissionTitle}</p>
            <p className="text-xs text-neutral-500">
              {entry.kind} • {entry.rawFileName || "Unknown file"}
            </p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${status.className}`}
        >
          {status.icon}
          {status.label}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-neutral-400">{formatSize(entry.rawFileSize)}</td>
      <td className="py-3 px-4 text-sm text-neutral-400">
        {entry.durationSec ? formatDuration(entry.durationSec) : "—"}
      </td>
      <td className="py-3 px-4 text-sm text-neutral-400">{entry.resolution || "—"}</td>
      <td className="py-3 px-4">
        {entry.encodingError && (
          <p className="text-xs text-red-400 truncate max-w-[200px]" title={entry.encodingError}>
            {entry.encodingError}
          </p>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost-sm" size="sm" onClick={handleView} className="text-neutral-400">
            View
          </Button>
          {showRetry && onRetry && (
            <Button
              variant="ghost-sm"
              size="sm"
              onClick={() => {
                void handleRetry();
              }}
              disabled={retrying}
              className="text-amber-400 hover:text-amber-300"
            >
              {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1">Retry</span>
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Table for displaying encoding entries
 */
interface EncodingTableProps {
  entries: EncodingEntry[];
  title: string;
  description?: string;
  emptyMessage: string;
  onRetry?: (id: string) => Promise<boolean>;
  onView?: (submissionId: string) => void;
  showRetry?: boolean;
}

function EncodingTable({
  entries,
  title,
  description,
  emptyMessage,
  onRetry,
  onView,
  showRetry = false,
}: Readonly<EncodingTableProps>) {
  return (
    <Card className="bg-transparent border-neutral-800">
      <CardHeader>
        <CardTitle className="text-lg text-white">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-center text-neutral-500 py-8">{emptyMessage}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-700 text-left">
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Submission
                  </th>
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">Status</th>
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">Size</th>
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">Duration</th>
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Resolution
                  </th>
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">Error</th>
                  <th className="py-2 px-4 text-xs font-medium text-neutral-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <EncodingEntryRow
                    key={entry.id}
                    entry={entry}
                    onRetry={onRetry}
                    onView={onView}
                    showRetry={showRetry}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Get polling indicator CSS class based on variant
 */
function getPollingIndicatorClass(variant: StatusVariant): string {
  switch (variant) {
    case "success":
      return "bg-emerald-400 animate-pulse";
    case "warning":
      return "bg-amber-400";
    case "error":
      return "bg-red-400";
    default:
      return "bg-neutral-400";
  }
}

/**
 * Encoding Dashboard Page
 *
 * Provides visibility into the encoding pipeline with:
 * - Queue statistics (length, success rate, avg time)
 * - Active/in-progress encodings
 * - Failed encodings with retry capability
 * - Recently completed encodings
 * - Auto-refresh via polling (15s interval)
 */
export function EncodingDashboard(): ReactElement {
  const navigate = useNavigate();
  const { data, loading, error, isPolling, isPaused, consecutiveErrors, refresh, pause, resume, reset, retryEncoding } =
    useEncodingStatus();

  // Navigation to submission detail
  const handleViewSubmission = useCallback(
    (submissionId: string) => {
      navigate(`/admin/review/${submissionId}`);
    },
    [navigate],
  );

  // Polling status indicator
  const pollingStatus = useMemo(() => {
    if (error && consecutiveErrors >= 5) {
      return { label: "Stopped (errors)", variant: "error" as const };
    }
    if (isPaused) {
      return { label: "Paused", variant: "warning" as const };
    }
    if (isPolling) {
      return { label: "Refreshing...", variant: "success" as const };
    }
    return { label: `Auto-refresh ${POLLING_INTERVALS.ACTIVE_QUEUE / 1000}s`, variant: "default" as const };
  }, [error, consecutiveErrors, isPaused, isPolling]);

  // Loading state
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-lumi-accent" />
          <p className="text-neutral-400">Loading encoding status...</p>
        </div>
      </div>
    );
  }

  // Error state (with no data)
  if (error && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={reset} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Encoding Dashboard</h1>
          <p className="text-sm text-neutral-400 pb-6">Monitor video encoding pipeline and manage failed jobs</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Polling status */}
          <div className="flex items-center gap-2 text-sm">
            <span className={`inline-flex h-2 w-2 rounded-full ${getPollingIndicatorClass(pollingStatus.variant)}`} />
            <span className="text-neutral-400">{pollingStatus.label}</span>
          </div>

          {/* Pause/Resume button */}
          {isPaused ? (
            <Button onClick={resume} variant="ghost-sm" size="sm">
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
          ) : (
            <Button onClick={pause} variant="ghost-sm" size="sm">
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
          )}

          {/* Manual refresh */}
          <Button
            onClick={() => {
              void refresh();
            }}
            variant="ghost-sm"
            size="sm"
            disabled={isPolling}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isPolling ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error banner (when we have data but polling is failing) */}
      {error && data && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div>
              <p className="text-red-400 font-medium">Polling error</p>
              <p className="text-sm text-red-400/70">
                {error} ({consecutiveErrors} consecutive errors)
              </p>
            </div>
          </div>
          <Button onClick={reset} variant="ghost-sm" size="sm" className="border-red-700 text-red-400">
            Reset
          </Button>
        </div>
      )}

      {/* Stats Panel */}
      {data && <EncodingStatsPanel stats={data.stats} />}

      {/* Active Encodings */}
      {data && (
        <EncodingTable
          entries={data.active}
          title="Active Encodings"
          description="Videos currently being processed or waiting in queue"
          emptyMessage="No active encodings in the queue"
          onView={handleViewSubmission}
        />
      )}

      {/* Failed Encodings */}
      {data && (
        <EncodingTable
          entries={data.failed}
          title="Failed Encodings"
          description="Videos that failed to encode - retry or investigate errors"
          emptyMessage="No failed encodings"
          onRetry={retryEncoding}
          onView={handleViewSubmission}
          showRetry
        />
      )}

      {/* Recently Completed */}
      {data && (
        <EncodingTable
          entries={data.recentlyCompleted}
          title="Recently Completed"
          description="Last 10 successfully encoded videos"
          emptyMessage="No recently completed encodings"
          onView={handleViewSubmission}
        />
      )}
    </div>
  );
}
